/**
 * Step 7: Report
 * Results vs. projections using DSP/SSP-measurable metrics only:
 *   impressions, reach, completion rate, CPM, frequency.
 * Simple timeline chart, benchmark context.
 * Two return paths: "Run Again" and "New Campaign".
 * Uses SelfServeHeader + ShadCN Card, Badge, Separator, Button.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SelfServeHeader from '@/components/mediaplan/SelfServeHeader';
import ErrorCard from '@/components/mediaplan/ErrorCard';
import { ReportSkeleton } from '@/components/mediaplan/ScreenSkeleton';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  MapPin,
  Users,
  Eye,
  ArrowRight,
  RefreshCw,
  BarChart3,
  Target,
  DollarSign,
  Repeat,
} from 'lucide-react';
import mediaPlanApi from '@/services/mediaPlanApi';
import useMediaPlanFlow from '@/hooks/useMediaPlanFlow';

function MetricCard({ label, value, projected, icon: Icon, accent = false }) {
  const isAboveProjection = projected && typeof value === 'number' && value > projected.max;
  const isInRange = projected && typeof value === 'number' && value >= projected.min && value <= projected.max;

  return (
    <div className={cn('rounded-xl p-4', accent ? 'bg-teal/5' : 'bg-muted')}>
      <div className="flex items-center gap-1.5 mb-2">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
      <div className={cn('text-2xl font-bold tracking-tight', accent && 'text-teal-700')}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      {projected && (
        <div className="flex items-center gap-1 mt-1">
          {isAboveProjection ? (
            <TrendingUp className="h-3 w-3 text-teal-600" />
          ) : isInRange ? (
            <Target className="h-3 w-3 text-teal-600" />
          ) : (
            <TrendingDown className="h-3 w-3 text-coral" />
          )}
          <span className={cn(
            'text-[11px] font-medium',
            isAboveProjection || isInRange ? 'text-teal-600' : 'text-coral'
          )}>
            {isAboveProjection ? 'Above' : isInRange ? 'On target' : 'Below'} projection ({projected.min.toLocaleString()}-{projected.max.toLocaleString()})
          </span>
        </div>
      )}
    </div>
  );
}

function MiniChart({ data, height = 60 }) {
  const max = Math.max(...data.map((d) => d.impressions));
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * 100;
    const y = 100 - (d.impressions / max) * 100;
    return `${x},${y}`;
  }).join(' ');

  const fillPoints = `0,100 ${points} 100,100`;

  return (
    <div className="w-full" style={{ height }}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        <polygon points={fillPoints} fill="url(#chartGradient)" opacity="0.15" />
        <polyline
          points={points} fill="none" stroke="url(#chartLineGradient)"
          strokeWidth="1.5" vectorEffect="non-scaling-stroke"
        />
        <defs>
          <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4ECDC4" />
            <stop offset="100%" stopColor="#4ECDC4" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="chartLineGradient" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#FF6B6B" />
            <stop offset="100%" stopColor="#4ECDC4" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

export default function CampaignReport() {
  const navigate = useNavigate();
  const { planId } = useParams();
  const flow = useMediaPlanFlow();

  const campaignId = flow.campaignId || planId;

  const [report, setReport] = useState(null);
  const [plan, setPlan] = useState(flow.plan || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const businessName = flow.business?.name || 'Your Business';

  // Load data on mount
  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      setLoading(true);
      setError(null);
      try {
        // Load report and plan in parallel
        const promises = [mediaPlanApi.getCampaignReport(campaignId)];
        if (!flow.plan && planId) {
          promises.push(mediaPlanApi.getPlan(planId));
        }

        const results = await Promise.all(promises);
        if (!cancelled) {
          setReport(results[0]);
          if (results[1]) setPlan(results[1]);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load report');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();
    return () => { cancelled = true; };
  }, [campaignId, planId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRunAgain = () => {
    // Set business/plan in flow context and navigate to plan page
    if (flow.business) flow.setBusiness(flow.business);
    if (plan) flow.selectPlan(plan.id, plan);
    navigate(`/campaign/${planId}/plan`);
  };

  const handleNewCampaign = () => {
    flow.reset();
    navigate('/campaign/new');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <SelfServeHeader businessName={businessName} />
        <div className="max-w-3xl mx-auto px-6 py-8">
          <ReportSkeleton />
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-background">
        <SelfServeHeader businessName={businessName} />
        <ErrorCard
          message={error || 'Report data not available'}
          onRetry={() => window.location.reload()}
          fallbackPath="/dashboard"
          fallbackLabel="Back to Dashboard"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SelfServeHeader businessName={businessName} />

      <div className="max-w-3xl mx-auto px-6 py-8 pb-36">
        {/* Hero */}
        <div className="mb-8 animate-[fadeInUp_0.45s_ease_both]">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant={report.status === 'completed' ? 'secondary' : 'outline'} className={cn(
              report.status !== 'completed' && 'bg-teal/10 text-teal-700 border-teal/30'
            )}>
              {report.status === 'completed' ? 'Completed' : 'In Progress'}
            </Badge>
          </div>
          <h1 className="text-2xl font-bold tracking-tight mb-1">{plan?.name || 'Campaign Report'}</h1>
          <div className="flex gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              {new Date(report.dateRange.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(report.dateRange.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
            {plan?.audience?.radius && (
              <span className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" />
                {plan.audience.radius}mi radius
              </span>
            )}
          </div>
        </div>

        {/* Performance vs Projections */}
        <Card className="mb-6 animate-[fadeInUp_0.5s_ease_both_0.06s]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Performance Results</CardTitle>
            <CardDescription>Actuals vs. your pre-campaign projections</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <MetricCard
                label="Impressions"
                value={report.actuals.impressions}
                projected={report.projections.impressions}
                icon={Eye}
              />
              <MetricCard
                label="Reach"
                value={report.actuals.reach}
                projected={report.projections.reach}
                icon={Users}
                accent
              />
              <MetricCard
                label="Completion Rate"
                value={`${report.actuals.completionRate}%`}
                projected={{ min: report.projections.completionRate, max: report.projections.completionRate }}
                icon={BarChart3}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <MetricCard
                label="CPM"
                value={`$${report.actuals.cpm.toFixed(2)}`}
                projected={report.projections.cpm}
                icon={DollarSign}
              />
              <MetricCard
                label="Frequency"
                value={`${report.actuals.frequency}x`}
                projected={report.projections.frequency}
                icon={Repeat}
              />
            </div>
          </CardContent>
        </Card>

        {/* Timeline Chart */}
        <Card className="mb-6 animate-[fadeInUp_0.5s_ease_both_0.12s]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Daily Performance</CardTitle>
            <CardDescription>Impressions over the campaign period</CardDescription>
          </CardHeader>
          <CardContent>
            <MiniChart data={report.timeline} height={120} />

            <div className="flex items-center justify-between mt-3 text-[11px] text-muted-foreground">
              <span>{new Date(report.dateRange.start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              <span>Weekend peaks visible -- heaviest on Fri-Sun as planned</span>
              <span>{new Date(report.dateRange.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
            </div>
          </CardContent>
        </Card>

        {/* Benchmark context */}
        <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-none text-white mb-8 animate-[fadeInUp_0.5s_ease_both_0.18s]">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-white">How You Compare</CardTitle>
            <CardDescription className="text-slate-400">Your campaign vs. industry benchmarks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Your Completion</div>
                <div className="text-xl font-bold">{report.actuals.completionRate}%</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Industry Avg</div>
                <div className="text-xl font-bold text-slate-400">{report.benchmarks.industryAvgCompletion}%</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Your Edge</div>
                <div className="text-xl font-bold text-teal">{report.benchmarks.performanceVsIndustry}</div>
              </div>
            </div>
            <Separator className="my-4 bg-slate-700" />
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Your CPM</div>
                <div className="text-lg font-bold">${report.actuals.cpm.toFixed(2)}</div>
                <div className="text-[11px] text-teal mt-0.5">{report.benchmarks.cpmVsIndustry}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Industry Avg CPM</div>
                <div className="text-lg font-bold text-slate-400">${report.benchmarks.industryAvgCpm}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Return paths */}
        <div className="flex gap-3 animate-[fadeInUp_0.5s_ease_both_0.24s]">
          <Button
            className="flex-1 bg-coral hover:bg-coral/90 text-white shadow-[0_2px_10px_rgba(255,107,107,0.35)] h-11 gap-2"
            onClick={handleRunAgain}
          >
            <RefreshCw className="h-4 w-4" />
            Run Again
          </Button>
          <Button
            variant="outline"
            className="flex-1 h-11 gap-2"
            onClick={handleNewCampaign}
          >
            New Campaign
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-center text-[11px] text-muted-foreground mt-2">
          "Run Again" reorders the same creative and skips to payment.
        </p>
      </div>
    </div>
  );
}
